/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewChild,
  DOCUMENT,
  ChangeDetectionStrategy
} from '@angular/core';
import {StatefulComponent} from '../../stateful/stateful.component';
import {PathAlertsState} from './path-alerts.state';
import {PathTypeEnum} from '../models/path-type.enum';
import {PathAlertsModel} from '../models/path-alerts.model';
import {PathAlertsEntityModel} from '../models/path-alerts-entity.model';
import {PathModel} from '../models/path.model';
import {merge, Observable, of, Subscription} from 'rxjs';
import {KeyValue} from '@angular/common';
import {PathAlertsService} from '../service/path-alerts.service';
import {filter} from 'rxjs/operators';

interface PathAlertsPositionModel {
    top?: string;
    left?: string;
    right?: string;
    bottom?: string;
    'max-width'?: string;
    'max-height'?: string;
    width?: string;
    height?: string;
}

@Component({
    selector: 'app-path-alerts',
    templateUrl: './path-alerts.component.html',
    styleUrls: ['./path-alerts.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PathAlertsComponent extends StatefulComponent<PathAlertsState> implements AfterViewInit, OnChanges, OnInit, OnDestroy {

    @Input() paths: PathAlertsModel[] = [];
    @Input() hasViewPathButton = false;
    @Input() hasEditPathButton = false;
    @Input() hasViewEntityButton = false;
    @Input() hasEditEntityButton = false;
    @Input() alternateStyle = false;
    @Input() forceCloseEvent: Observable<void> = of(undefined);
    @Input() forceOpenEvent: Observable<void> = of(undefined);
    @Input() secondaryAnchorRef: ElementRef;
    @Input() selectedSeparator: PathTypeEnum;
    @Output() viewEntityClick: EventEmitter<any> = new EventEmitter<any>();
    @Output() editEntityClick: EventEmitter<any> = new EventEmitter<any>();
    @Output() viewPathClick: EventEmitter<PathModel> = new EventEmitter<PathModel>();
    @Output() editPathClick: EventEmitter<PathModel> = new EventEmitter<PathModel>();
    @Output() componentState = new EventEmitter<PathAlertsState>();
    @Output() forceCloseFullLineTooltip = new EventEmitter<void>();
    @ViewChild('collapsedElement', {read: ElementRef}) collapsedElement: ElementRef;
    @ViewChild('expandedElement', {read: ElementRef}) expandedElement: ElementRef;

    // <editor-fold desc="Icon CSS constants">
    public readonly CSS_ITEM_OPEN = 'iwp-icon-gen_arrow_down';
    public readonly CSS_ITEM_CLOSED = 'iwp-icon-gen_arrow_right';
    public readonly CSS_BUTTON_CLOSE = 'iwp-icon-gen_close';
    public readonly CSS_BUTTON_EDIT = 'iwp-icon-gen_edit';
    public readonly CSS_BUTTON_VIEW = 'iwp-icon-gen_show_all';
    // </editor-fold>

    private subscriptions = new Subscription();
    /**
     * <p>This is a cache to be used on the separator click, which force opens the line when the component first expands
     */
    private lastIsOpen = this.isOpen;

    /**
     * Cache for the {@link separators} getter.
     */
    private _separators: Map<PathTypeEnum, PathAlertsModel> = new Map<PathTypeEnum, PathAlertsModel>();

    get separators(): Map<PathTypeEnum, PathAlertsModel> {
        return this._separators;
    }

    get isOpen(): boolean {
        return this.state?.openSeparator != null;
    }

    get alternateClass(): string {
        let alternateClass = '';

        if (this.alternateStyle) {
            alternateClass = 'alternate';
        }

        return alternateClass;
    }

    get expandedElementPosition(): PathAlertsPositionModel {
        const position: PathAlertsPositionModel = {};

        const finalAnchorHeight = this.secondaryAnchorRectangle.bottom < this.bodyRectangle.bottom
                ? this.secondaryAnchorRectangle.bottom - 8
                : this.bodyRectangle.bottom;
        position['max-height'] = `${finalAnchorHeight - this.componentRectangle.top}px`;
        position.top = `${this.componentRectangle.top}px`;
        // -16px to compensate for the components margins and paddings
        position.right = `calc(${this.bodyRectangle.width - this.componentRectangle.right}px - 16px)`;

        return position;
    }

    private get bodyRectangle(): DOMRect {
        return this.document.body.getBoundingClientRect();
    }

    private get secondaryAnchorRectangle(): DOMRect {
        return this.secondaryAnchorRef?.nativeElement?.getBoundingClientRect() ?? {};
    }

    private get componentRectangle(): DOMRect {
        return this.componentRef?.nativeElement?.getBoundingClientRect() ?? {};
    }

    constructor(@Inject(DOCUMENT) private document: Document,
                private componentRef: ElementRef,
                private pathAlerts: PathAlertsService) {
        super();
    }

    @Input() forceOpenLineCallback = (): void => {
    };

    // </editor-fold>

    // <editor-fold desc="Data-cy functions">

    ngAfterViewInit(): void {
        this.expandedElement?.nativeElement?.addEventListener('click', (event: Event) => {
            event.stopPropagation();
        });

        this.subscriptions.add(merge(
                this.forceCloseEvent,
                this.pathAlerts.openEvent.pipe(filter((component) => component !== this))
        )
                .subscribe(() => {
                    this.close();
                    this.paths.forEach((entry) => entry.entities = []);
                }));
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.pathsChangeHandler(changes.paths);
    }

    ngOnInit(): void {
        this.storeState({openSeparator: this.selectedSeparator});
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    // <editor-fold desc="Component functions">
    pathTypeCount(path: PathAlertsModel): number {
        let count = 0;

        if (path?.type?.count != null) {
            count = path.type.count;
        }
        return count;
    }

    close(): void {
        this.separatorClick(null);
    }

    separatorLabel(separator: KeyValue<PathTypeEnum, PathAlertsModel>): string {
        return `(${this.pathTypeCount(separator.value)}) ${separator.value.type.name}`;
    }

    // </editor-fold>

    // <editor-fold desc="Separator functions">
    separatorClick(separator: PathTypeEnum): void {
        if (!this.isSeparatorActive(separator)) {
            this.storeState({openSeparator: separator, openEntities: []});

            if (this.isOpen) {
                this.pathAlerts.openEvent.emit(this);
            }

            if (!this.lastIsOpen && this.isOpen) {
                this.forceOpenLineCallback();
            }
            this.lastIsOpen = this.isOpen;

            this.componentState.emit(this.state);
        } else {
            this.close();
        }
    }

    isSeparatorActive(separator: PathTypeEnum): boolean {
        return this.isOpen && this.state.openSeparator === separator;
    }

    separatorIconClass(separator: PathTypeEnum): string {
        let cssClass: string = this.CSS_ITEM_CLOSED;

        if (this.isSeparatorActive(separator)) {
            cssClass = this.CSS_ITEM_OPEN;
        }

        return cssClass;
    }

    separatorActiveClass(separator: PathTypeEnum): string {
        let cssClass = '';

        if (this.isSeparatorActive(separator)) {
            cssClass = 'active';
        }

        return cssClass;
    }

    // </editor-fold>

    // <editor-fold desc="Accordion functions">
    isAccordionActive(entity: PathAlertsEntityModel): boolean {
        return this.state.openEntities?.includes(entity);
    }

    lineClick(entity: PathAlertsEntityModel): void {
        let openEntities: PathAlertsEntityModel[] = [];

        if (this.state.openEntities != null) {
            openEntities = [...this.state.openEntities];
        }

        if (this.isAccordionActive(entity)) {
            openEntities = openEntities.filter((e) => e !== entity);
        } else {
            openEntities.push(entity);
        }

        this.storeState({openEntities});
    }

    accordionIconClass(entity: PathAlertsEntityModel): string {
        let cssClass: string = this.CSS_ITEM_CLOSED;

        if (this.isAccordionActive(entity)) {
            cssClass = this.CSS_ITEM_OPEN;
        }

        return cssClass;
    }

    accordionActiveClass(entity: PathAlertsEntityModel): string {
        let cssClass = '';

        if (this.isAccordionActive(entity)) {
            cssClass = 'active';
        }

        return cssClass;
    }

    lastAccordionStyle(isLast: boolean): string {
        return isLast ? 'is-last' : '';
    }

    // </editor-fold>

    // <editor-fold desc="Entity functions">
    viewEntity = (path: PathModel, entity: PathAlertsEntityModel, event: Event): void => {
        event.stopPropagation();
        this.viewEntityClick.emit(entity);
    };

    editEntity = (path: PathModel, entity: PathAlertsEntityModel, event: Event): void => {
        event.stopPropagation();
        this.editEntityClick.emit(entity);
    };

    entityLabel(entity: PathAlertsEntityModel): string {
        const pathCount = entity?.paths?.length ?? 0;
        const entityName = entity?.displayLabel ?? '-';

        return `(${pathCount}) ${entityName}`;
    }

    // </editor-fold>

    // <editor-fold desc="Path functions">
    viewPath = (path: PathModel, entity: PathAlertsEntityModel, event: Event): void => {
        event.stopPropagation();
        this.viewPathClick.emit(path);
    };

    editPath = (path: PathModel, entity: PathAlertsEntityModel, event: Event): void => {
        event.stopPropagation();
        this.editPathClick.emit(path);
    };

    pathLabel(path: PathModel): string {
        return this.flattenPath(path)
                .join(' > ');
    }

    separatorDataCy(key: string): string {
        return `path-alerts-separator-${key}`;
    }

    separatorIndicatorDataCy(key: string): string {
        return `${this.separatorDataCy(key)}-indicator`;
    }

    separatorLabelDataCy(key: string): string {
        return `${this.separatorDataCy(key)}-label`;
    }

    separatorStatusDotDataCy(key: string): string {
        return `${this.separatorDataCy(key)}-status-dot`;
    }

    entityIndicatorDataCy(code: string): string {
        return `${this.entityLineDataCy(code)}-indicator`;
    }

    entityLineDataCy(code: string): string {
        return `path-alerts-line-entity-${code}`;
    }

    pathLineDataCy(entityCode: string, pathIndex: number): string {
        return `path-alerts-line-path-${entityCode}-${pathIndex}`;
    }

    lineElementLabelDataCy(baseDataCy: string): string {
        return `${baseDataCy}-label`;
    }

    lineElementViewButtonDataCy(baseDataCy: string): string {
        return `${baseDataCy}-view-button`;
    }

    lineElementEditButtonDataCy(baseDataCy: string): string {
        return `${baseDataCy}-edit-button`;
    }

    lineAccordionDataCy(code: string): string {
        return `${this.entityLineDataCy(code)}-accordion`;

    }

    /**
     * <p>Handles changes over the {@link paths} input property
     * @param pathsChange the {@link paths} change
     */
    private pathsChangeHandler(pathsChange: SimpleChange): void {
        if (pathsChange != null) {
            if (pathsChange.firstChange || pathsChange.currentValue !== pathsChange.previousValue) {
                this._separators = new Map(this.paths?.map((path): [PathTypeEnum, PathAlertsModel] => [path.type.code, path]));
            }
        }
    }

    private flattenPath(path: PathModel): string[] {
        if (path == null) {
            return [];
        }

        if (path.parent == null) {
            return [path.displayLabel];
        }

        return this.flattenPath(path.parent)
                .concat(path.displayLabel);
    }

    // </editor-fold>

    @HostListener('click', ['$event'])
    stopComponentClickPropagation(event: Event): void {
        event.stopPropagation();
    }
}
